import { useRef, type CSSProperties, type PointerEvent, type ReactNode } from 'react'
import { byId } from '../data/catalog'
import { fishStage } from '../domain/economy'
import { useAppStore } from '../store'
import type { CatalogueItem, FishInstance, Placement } from '../types'

export type CoveMode = 'idle' | 'focus' | 'paused' | 'break' | 'completion'

/** New reef artwork, with stable legacy collection IDs retained for saved fish. */
const reefArtwork: Record<string, string> = {
  'fish-guppy': '/art/fish/rework-v2/hero-orange/approved-master.png',
  'fish-platy': '/art/fish/reef-approved-v1/flame-angelfish/approved-master.png',
  'fish-neon': '/art/fish/reef-approved-v1/royal-gramma/approved-master.png',
  'fish-danio': '/art/fish/reef-approved-v1/royal-gramma/approved-master.png',
  'fish-cory': '/art/fish/reef-approved-v1/clown-triggerfish/approved-master.png',
  'fish-gourami': '/art/fish/reef-approved-v1/flame-angelfish/approved-master.png',
  'fish-betta': '/art/fish/rework-v2/hero-orange/approved-master.png',
  'fish-angel': '/art/fish/rework-v2/moorish-idol/approved-master.png',
  'fish-gold': '/art/fish/reef-approved-v1/clown-triggerfish/approved-master.png',
  'fish-clown': '/art/fish/rework-v2/clownfish/approved-master.png',
}

function FishVector({ item, stage }: { item: CatalogueItem; stage: 1 | 2 | 3 }) {
  const size = stage === 1 ? 0.78 : stage === 2 ? 0.9 : 1
  return (
    <img className="fish-svg reef-fish-image" src={reefArtwork[item.id] ?? reefArtwork['fish-guppy']}
      draggable={false} alt={`${item.name}, growth stage ${stage}`}
      style={{ transform: `scale(${size})` }} />
  )
}

function PlantVector({ item }: { item: CatalogueItem }) {
  const [base, accent, light] = item.colors
  const grass = item.shape === 'grass' || item.shape === 'fern'
  const coral = item.shape === 'coral'
  return (
    <svg viewBox="0 0 160 210" className="plant-svg" role="img" aria-label={item.name}>
      <ellipse cx="80" cy="198" rx="52" ry="10" fill="#153f42" opacity=".28" />
      {coral ? (
        <g fill="none" stroke={base} strokeWidth="15" strokeLinecap="round">
          <path d="M79 197V61m0 48L40 76m39 13 40-48m-18 82 32-24M58 139 27 116" />
          <g stroke={accent} strokeWidth="7"><path d="M79 187V67m0 47L43 80m36 14 38-46" /></g>
        </g>
      ) : (
        <g className="plant-sway">
          {[0, 1, 2, 3, 4].map((index) => {
            const x = 35 + index * 22
            const tilt = (index - 2) * 11
            return (
              <g key={index} transform={`rotate(${tilt} ${x} 196)`}>
                <path d={`M${x} 197 Q ${x - 15} 118 ${x + (grass ? 5 : 22)} ${30 + index * 10}`} fill="none" stroke={base} strokeWidth={grass ? 10 : 7} strokeLinecap="round" />
                {!grass && <ellipse cx={x + 8} cy={86 + index * 6} rx="23" ry="39" transform={`rotate(${22 + tilt} ${x + 8} ${86 + index * 6})`} fill={index % 2 ? accent : light} opacity=".86" />}
              </g>
            )
          })}
        </g>
      )}
    </svg>
  )
}

function DecorVector({ item }: { item: CatalogueItem }) {
  const [base, accent, light] = item.colors
  const common = <><ellipse cx="80" cy="150" rx="64" ry="17" fill="#143b40" opacity=".24" /><path d="M24 143 42 73l42-23 49 30 13 63Z" fill={base} /><path d="m43 91 42-24 35 22-12 13-61 8Z" fill={accent} opacity=".78" /></>
  const shapes: Record<string, ReactNode> = {
    pebbles: <g>{[25, 50, 78, 108, 135].map((x, i) => <ellipse key={x} cx={x} cy={136 - (i % 2) * 10} rx={20 - i} ry={13 + (i % 3)} fill={[base, accent, light][i % 3]} />)}</g>,
    rock: <g>{common}<path d="M57 143q4-49 42-47 34 4 37 47Z" fill="#173e42" opacity=".7" /></g>,
    arch: <g><path d="M25 146V65h32v81m46 0V65h32v81M43 78q37-62 75 0" fill="none" stroke={base} strokeWidth="24" strokeLinecap="round" /><path d="M33 82q47-74 94 0" fill="none" stroke={light} strokeWidth="5" opacity=".45" /></g>,
    wood: <g><path d="M16 142q52-46 127-78" stroke={base} strokeWidth="24" strokeLinecap="round" /><path d="m79 105-20-48m56 34 26-39" stroke={accent} strokeWidth="15" strokeLinecap="round" /></g>,
    tunnel: <g>{common}<path d="M47 146q4-59 39-64 39 6 45 64Z" fill="#263c3c" /></g>,
    shell: <g><path d="M37 142q4-91 54-101 48 17 39 101Z" fill={base} /><path d="M85 43v99M57 57l17 85m38-83-15 83" stroke={light} strokeWidth="6" opacity=".62" /></g>,
    sand: <path d="M12 146q22-47 65-33 45-23 73 33Z" fill={base} />,
    ruin: <g><path d="M25 145V48h34v97m43 0V48h34v97" fill={base} /><path d="M42 70q40-58 77 0" fill="none" stroke={accent} strokeWidth="25" /></g>,
    lighthouse: <g><path d="m55 145 12-98h45l14 98Z" fill={base} /><path d="M62 82h57M67 48l10-25h25l12 25" stroke={accent} strokeWidth="12" /><circle cx="90" cy="60" r="10" fill={light} className="lantern-glow" /></g>,
    house: <g><path d="M28 145V75l52-39 53 39v70Z" fill={base} /><path d="m20 79 60-52 61 52" stroke={accent} strokeWidth="14" fill="none" /><path d="M68 145v-44h29v44" fill="#1c4a4b" /></g>,
    book: <g><path d="m22 69 58 15 58-15v73l-58 14-58-14Z" fill={base} /><path d="M80 85v70M34 86l35 8m57-8-35 8" stroke={light} strokeWidth="5" /></g>,
    compass: <g><circle cx="80" cy="99" r="58" fill={base} /><circle cx="80" cy="99" r="42" fill="none" stroke={light} strokeWidth="7" /><path d="m80 57 13 42-13 42-13-42Z" fill={accent} /></g>,
    moongate: <g><circle cx="80" cy="92" r="56" fill="none" stroke={base} strokeWidth="19" /><path d="M12 150h136" stroke={accent} strokeWidth="22" strokeLinecap="round" /></g>,
    chest: <g><path d="M24 84h112v62H24z" fill={base} /><path d="M24 84q8-45 56-45t56 45" fill={accent} /><path d="M72 76h16v37H72z" fill={light} /></g>,
    lantern: <g><path d="M51 62h58l12 72H39Z" fill={base} /><path d="M59 26h42l9 37H50Z" fill="none" stroke={accent} strokeWidth="10" /><ellipse cx="80" cy="98" rx="24" ry="30" fill={light} className="lantern-glow" /></g>,
  }
  return <svg viewBox="0 0 160 170" className="decor-svg" role="img" aria-label={item.name}>{shapes[item.shape] ?? common}</svg>
}

function PositionedItem({
  placement,
  editable,
  onMove,
}: {
  placement: Placement
  editable: boolean
  onMove: (itemId: string, clientX: number, clientY: number) => void
}) {
  const item = byId.get(placement.itemId)
  if (!item) return null
  const style = { left: `${placement.x}%`, top: `${placement.y}%`, transform: `translate(-50%, -100%) scale(${placement.scale})` }

  function move(event: PointerEvent<HTMLDivElement>) {
    if (!editable) return
    event.preventDefault()
    onMove(placement.itemId, event.clientX, event.clientY)
  }

  return (
    <div
      className={`cove-item layer-${placement.layer} ${editable ? 'cove-item-editable' : ''}`}
      style={style}
      onPointerDown={(event) => {
        if (!editable) return
        event.currentTarget.setPointerCapture(event.pointerId)
        move(event)
      }}
      onPointerMove={(event) => {
        if (editable && event.currentTarget.hasPointerCapture(event.pointerId)) move(event)
      }}
      onPointerUp={(event) => {
        if (editable && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
      }}
      onPointerCancel={(event) => {
        if (editable && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
      }}
      aria-label={editable ? `Move ${item.name}` : undefined}
    >
      {item.category === 'plant' ? <PlantVector item={item} /> : <DecorVector item={item} />}
    </div>
  )
}

function SwimmingFish({ fish, index, reduced }: { fish: FishInstance; index: number; reduced: boolean }) {
  const item = byId.get(fish.catalogueId)
  if (!item) return null
  const vars = {
    '--fish-y': `${18 + ((index * 19) % 48)}%`,
    '--fish-delay': `${-index * 2.7}s`,
    '--fish-duration': `${9 + (index % 4) * 2.4}s`,
    '--fish-scale': `${0.66 + (index % 3) * 0.12}`,
    left: reduced ? `${18 + ((index * 22) % 66)}%` : undefined,
  } as CSSProperties
  return <div className={`swimming-fish fish-${index % 3}`} style={vars}><FishVector item={item} stage={fishStage(fish.feedTotal)} /></div>
}

export function Aquarium({
  mode = 'idle',
  compact = false,
  editable = false,
}: {
  mode?: CoveMode
  compact?: boolean
  editable?: boolean
}) {
  const { state, movePlacement } = useAppStore()
  const aquariumRef = useRef<HTMLElement | null>(null)

  function moveItem(itemId: string, clientX: number, clientY: number) {
    if (!editable || !aquariumRef.current) return
    const rect = aquariumRef.current.getBoundingClientRect()
    const x = ((clientX - rect.left) / rect.width) * 100
    const y = ((clientY - rect.top) / rect.height) * 100
    movePlacement(itemId, x, y)
  }
  const background = byId.get(state.activeBackground) ?? byId.get('bg-seaglass')!
  const reduced = state.settings.reducedMotion || (mode === 'focus' && state.settings.staticFocus)
  const fishLimit = state.settings.quality === 'low' ? 5 : state.settings.quality === 'high' ? 12 : 8
  const bubbleCount = reduced ? 0 : state.settings.quality === 'low' ? 6 : state.settings.quality === 'high' ? 22 : 12

  return (
    <section
      ref={aquariumRef}
      className={`aquarium aquarium-${mode} ${compact ? 'aquarium-compact' : ''} ${reduced ? 'reduced' : ''} ${editable ? 'aquarium-editable' : ''}`}
      style={{ '--water-top': background.colors[0], '--water-mid': background.colors[1], '--water-deep': background.colors[2] } as CSSProperties}
      aria-label="Your ThriveCove aquarium"
    >
      <div className="water-rays" />
      <div className="distant-rock rock-one" /><div className="distant-rock rock-two" />
      <div className="bubble-field" aria-hidden="true">
        {Array.from({ length: bubbleCount }, (_, index) => (
          <i key={index} style={{ '--bubble-x': `${7 + ((index * 29) % 88)}%`, '--bubble-delay': `${-(index % 9)}s`, '--bubble-size': `${4 + (index % 4) * 3}px` } as CSSProperties} />
        ))}
      </div>
      <div className="cove-layer cove-back">{state.placements.filter((p) => p.layer === 'back').map((p) => <PositionedItem key={p.itemId} placement={p} editable={editable} onMove={moveItem} />)}</div>
      <div className="cove-layer cove-fish">{state.fish.slice(0, fishLimit).map((fish, index) => <SwimmingFish key={fish.id} fish={fish} index={index} reduced={reduced} />)}</div>
      <div className="cove-layer cove-mid">{state.placements.filter((p) => p.layer === 'mid').map((p) => <PositionedItem key={p.itemId} placement={p} editable={editable} onMove={moveItem} />)}</div>
      <div className="substrate" />
      <div className="cove-layer cove-front">{state.placements.filter((p) => p.layer === 'front').map((p) => <PositionedItem key={p.itemId} placement={p} editable={editable} onMove={moveItem} />)}</div>
      {mode === 'completion' && !reduced && <div className="completion-ripple" aria-hidden="true" />}
    </section>
  )
}

export { FishVector, PlantVector, DecorVector }
